import { useEffect } from "react";
import ReactSelect from "react-select";
import CreatableSelect from "react-select/creatable";
import {
  func,
  string,
  number,
  bool,
  arrayOf,
  object,
  oneOfType,
  instanceOf,
} from "prop-types";

const DynamicSearchSelect = ({
  value,
  items,
  required,
  modifier,
  getItems,
  valueKey,
  labelKey,
  isMulti = false,
  showError = false,
  placeholder = "Seleccionar...",
  isDisabled = false,
  isCreatable = false,
  isLoading = false,
  onCreateOption,
  formatCreateLabel,
  noOptionsMessage,
  loadingMessage = "Cargando...",
}) => {
  const redBorder =
    showError && required && (!value || value === "" || value === null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = (query) => {
    if (typeof getItems === "function") {
      getItems({ query });
    }
  };

  const getOptions = () => {
    if (!Array.isArray(items)) return [];
    return items
      .filter((item) => item !== null && item !== undefined)
      .map((item) => {
        const value = item[valueKey || "value"];
        const label = item[labelKey || "label"] || String(value || "");
        return {
          value,
          label,
          color: item.color,
        };
      })
      .filter((option) => option.value !== null && option.value !== undefined && option.value !== "");
  };

  const getValue = (options) => {
    if (value === null || value === undefined || value === "")
      return isMulti ? [] : null;

    if (isMulti && Array.isArray(value)) {
      return value
        .map((v) => {
          if (v === null || v === undefined) return null;
          return options.find(
            (option) => option.value === (v?.value ?? v)
          );
        })
        .filter(Boolean);
    }

    return options.find((option) => option.value === value);
  };

  const renderSelect = () => {
    const options = getOptions();
    const selectedValue = getValue(options);
    const SelectComponent = isCreatable ? CreatableSelect : ReactSelect;

    const handleChange = (option) => {
      if (isMulti) {
        modifier(option ? option.map((o) => o.value) : []);
      } else {
        modifier(option?.value || null);
      }
    };

    const handleCreateOption = (inputValue) => {
      if (typeof onCreateOption === "function") {
        onCreateOption(inputValue);
      }
    };

    const selectStyles = {
      menuPortal: (base) => ({ ...base, zIndex: 10050 }),
    };

    // When an option carries a `color`, show it as a dot before the label.
    const formatOptionLabel = (option) =>
      option.color ? (
        <span className="d-flex align-items-center">
          <span
            className="d-inline-block rounded-circle me-2 flex-shrink-0"
            style={{
              width: 10,
              height: 10,
              backgroundColor: option.color,
            }}
          />
          {option.label}
        </span>
      ) : (
        option.label
      );

    return (
      <>
        <SelectComponent
          isSearchable
          isClearable
          isDisabled={isDisabled}
          isLoading={isLoading}
          loadingMessage={() => loadingMessage}
          value={selectedValue}
          options={options}
          className="px-0"
          isMulti={isMulti}
          placeholder={placeholder}
          onInputChange={fetchData}
          classNamePrefix={redBorder ? "border-danger " : ""}
          formatOptionLabel={formatOptionLabel}
          onChange={handleChange}
          menuPortalTarget={
            typeof document !== "undefined" ? document.body : null
          }
          styles={selectStyles}
          {...(isCreatable && {
            onCreateOption: handleCreateOption,
            formatCreateLabel: formatCreateLabel || ((inputValue) => `Crear: ${inputValue}`),
            noOptionsMessage: noOptionsMessage || (() => "No hay opciones, escribe para crear una nueva"),
          })}
        />
        {redBorder && (
          <p className="text-danger mb-0">Debes llenar este campo.</p>
        )}
      </>
    );
  };

  return <div>{renderSelect()}</div>;
};

DynamicSearchSelect.propTypes = {
  showLogs: bool,
  isMulti: bool,
  required: bool,
  getItems: func,
  showError: bool,
  isDisabled: bool,
  isCreatable: bool,
  isLoading: bool,
  loadingMessage: string,
  onCreateOption: func,
  formatCreateLabel: func,
  noOptionsMessage: func,
  valueKey: string,
  labelKey: string,
  placeholder: string,
  items: instanceOf(Array),
  modifier: func.isRequired,
  value: oneOfType([number, string, arrayOf(object)]),
};

export default DynamicSearchSelect;